/**
 * GovFormZone (gov-forms Build 4, D-132) — the task-drawer surface for a government form.
 * Renders the framing-appropriate primary action + the form's "have these ready" checklist
 * (requiredDocuments). The primary surface for gov forms (drawer-first, D3); the standalone
 * Estate Documents page stays the full reference.
 *
 * deliveryFraming drives the framing:
 *   real_fill     → "Download pre-filled form" (the 4 federal forms; server fills + companion page)
 *   fill_reviewed → "Prepare your answers" (court/probate forms; lawyer/paralegal review required
 *                   before filing — renders prep-style until the court-form build lands, s133)
 *   copy          → "Prepare your answers" (retired for the court batch; kept for back-compat)
 *   prep          → "Before you begin" (counter/web/phone; gather these, then start the process)
 */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { FileText, ExternalLink, Download, CheckSquare, ChevronDown, ChevronRight } from "lucide-react";
import { FILL_REVIEWED_REVIEW_COPY, DRAFT_ACK_ADVISORY_BY_FORM } from "@shared/constants/gov-form-framing";
import { PHASE_6B_ACTIVE_FORMS, parseMultiSelect } from "@shared/constants/form-field-tiers";
import { dedupeOrderedGroups } from "@shared/repeating-group-dedupe";
import { parseAssignment, parseMerged } from "@shared/record-assignment";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { AckDialog } from "./AckDialog";
import { LetterGapDialog, type LetterGapField } from "./LetterGapDialog";
import { SAGE, WARM } from "@shared/brand";

// Derive the download filename from the generate response (GOVFORM-ERROR-UX, s138). The server sets
// Content-Disposition; fall back to a slug of the form title + the correct extension by content-type
// (draft court forms are .docx, everything else .pdf). Pure — unit-tested.
export function downloadFilename(res: Response, fallbackBase: string): string {
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  if (m?.[1]) {
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }
  const ct = res.headers.get("Content-Type") ?? "";
  const ext = ct.includes("word") || ct.includes("officedocument") ? "docx" : "pdf";
  const base = fallbackBase.replace(/[^a-zA-Z0-9\s]/g, "").trim().replace(/\s+/g, "-").toLowerCase() || "form";
  return `${base}.${ext}`;
}


// s156 (SAVE-DETERMINISM-1): how long the "Saved" confirmation holds before the dialog closes. Long
// enough to answer "did that save?", short enough not to feel like a wait.
const SAVED_CUE_MS = 900;

export interface GovForm {
  formId: string;
  title: string;
  deliveryFraming: string | null;
  requiredDocuments: string[];
  sourceUrl: string | null;
  formVersion: string | null;
  documentType: string | null;
  supportsDraft?: boolean; // D-135: fill_reviewed court form served as a DRAFT .docx behind the ack gate
}

const FRAMING_COPY: Record<string, { heading: string; blurb: string; linkLabel: string }> = {
  real_fill: {
    heading: "Download your pre-filled form",
    blurb: "We fill what your plan already knows onto the official form. Review it, type anything left blank (like the SIN), then print and sign.",
    linkLabel: "View the official form",
  },
  fill_reviewed: {
    heading: "Prepare your answers",
    blurb: FILL_REVIEWED_REVIEW_COPY,
    linkLabel: "View the official form",
  },
  copy: {
    heading: "Prepare your answers",
    blurb: "This form is usually prepared with your lawyer. Have the details below ready so you can complete it quickly.",
    linkLabel: "View the official form",
  },
  prep: {
    heading: "Before you begin",
    blurb: "This is done at the counter or online. Gather these before you start.",
    linkLabel: "Start the process",
  },
  generated: {
    heading: "Download your preparation sheet",
    blurb: "We've laid out the return in order and filled the basic facts. The dollar values are left blank for you and your advisor to complete with date-of-death figures, then enter on the official portal.",
    linkLabel: "View the official form",
  },
};

// s147 (D-146) — the "Before you file this form" panel payload (GET …/estate-documents/:type/prep).
type PrepBlank = { fieldKey: string; label: string; tier: string; reason: string; where: string; group?: string };
type PrepPayload = {
  formId: string | null;
  blanks: PrepBlank[];
  filled: { fieldKey: string; label: string }[];
  executorSections: { section: string; detail: string }[];
  submission: { kind: string; line: string } | null;
  inkSignature?: string | null;
  companion?: string[]; // s149 SEAM-FIX-1: lossy-select cells the executor must pick ON the form (not covered by `blanks`)
  included: { fieldKey: string; label: string; selectedLabels: string[]; capacity?: number }[];
  reopenFields: LetterGapField[];
  initialValues: Record<string, string>;
};

// Presentation-layer grouping ONLY (the data stays per-cell for real totality — D-146). The 8 B3
// cells collapse to one human line; the SIN to one. Everything else lists per field.
const GROUP_TITLE: Record<string, { title: string; where: string }> = {
  applicant: { title: "Your name and address as the applicant", where: "enter them in Section C — the form keeps them in separate boxes we don't fill." },
  secure:    { title: "Your SIN", where: "enter it from your secure file." },
};
function groupBlanks(blanks: PrepBlank[]): Array<{ title: string; where: string }> {
  const seen = new Set<string>();
  const rows: Array<{ title: string; where: string }> = [];
  for (const b of blanks) {
    if (b.group) {
      if (seen.has(b.group)) continue;
      seen.add(b.group);
      rows.push(GROUP_TITLE[b.group] ?? { title: b.label, where: b.where });
    } else {
      rows.push({ title: b.label, where: b.where });
    }
  }
  return rows;
}

// s148 PANEL-TIMING-1 — the executor's CURRENT answers, echoed back BEFORE download, across EVERY prompt
// type (record_select → resolved names; multi_select → option labels; select/text → the value). Pure,
// client-derived from the prep payload (reopenFields + initialValues + the resolved record `included`) —
// no server change. Only fields with a STORED answer appear (a prior generation); an empty stored answer
// reads as an explicit choice ("none"/"left blank"), never as "unanswered".
// `overflow` (s148 CHILD-OVERFLOW-1) — the record_select names BEYOND the form's row capacity: they don't
// print on the form, they go on an attached sheet. Split at `capacity`, in FILL order (the prep endpoint
// emits selectedLabels in fill order), so the echo NEVER claims four names print when three do.
export type AnswerEcho = { key: string; label: string; value: string; overflow?: string };
export function deriveAnswerEcho(
  reopenFields: LetterGapField[],
  initialValues: Record<string, string>,
  included: { fieldKey: string; label: string; selectedLabels: string[]; capacity?: number }[],
): AnswerEcho[] {
  const incByKey = new Map(included.map((i) => [i.fieldKey, i]));
  // s149 one-section-per-child: de-dupe the selected records ACROSS sibling record_select fields with the
  // SHARED policy (first field in order wins — the SAME precedence the fill uses), so the echo reflects
  // the DE-DUPED fill split, not the raw stored answers. Resolve each field's kept rowIds to labels in
  // FILL order (recordOptions = the fill order), so a legacy double-selection matches the PDF with no
  // data migration. rowIds are globally unique, so one pass over all record_select fields is safe.
  const recordGroups = reopenFields
    .filter((f) => f.type === "record_select")
    .map((f) => {
      const sel = new Set(parseMultiSelect(initialValues[f.key] ?? ""));
      return { selectionKey: f.key, rowIds: (f.recordOptions ?? []).map((o) => o.key).filter((k) => sel.has(k)) };
    });
  const keptByKey = new Map(dedupeOrderedGroups(recordGroups).perGroup.map((g) => [g.selectionKey, g.kept]));

  const rows: AnswerEcho[] = [];
  for (const f of reopenFields) {
    const stored = initialValues[f.key];
    if (stored === undefined) continue; // no stored answer → not a prior choice
    let value: string;
    let overflow: string | undefined;
    if (f.type === "record_assign") {
      // s156 D pt.2(a) — ONE echo row per target grid, naming who lands there in the SAME order the fill
      // uses (recordOptions = the candidate pool's order = the print order). A record the executor
      // confirmed as a cross-section duplicate is excluded, exactly as the fan-out excludes it.
      const labelByKey = new Map((f.recordOptions ?? []).map((o) => [o.key, o.label]));
      const assignment = parseAssignment(stored);
      const merged = new Set(parseMerged(initialValues[f.mergeKey ?? ""] ?? ""));
      for (const t of f.assignTargets ?? []) {
        const names = (f.recordOptions ?? [])
          .filter((o) => !merged.has(o.key) && assignment[o.key] === t.gridKey)
          .map((o) => labelByKey.get(o.key) ?? o.key);
        rows.push({ key: `${f.key}:${t.gridKey}`, label: t.label, value: names.length ? names.join(", ") : "none" });
      }
      continue;
    }
    if (f.type === "record_select") {
      const labelByKey = new Map((f.recordOptions ?? []).map((o) => [o.key, o.label]));
      const labels = (keptByKey.get(f.key) ?? []).map((id) => labelByKey.get(id) ?? id);
      const cap = incByKey.get(f.key)?.capacity ?? f.capacity ?? 0;
      if (labels.length === 0) value = "none";
      else if (cap > 0 && labels.length > cap) { value = labels.slice(0, cap).join(", "); overflow = labels.slice(cap).join(", "); }
      else value = labels.join(", ");
    } else if (f.type === "multi_select" && f.multiOptions) {
      const labels = parseMultiSelect(stored).map((k) => f.multiOptions!.find((o) => o.key === k)?.label ?? k);
      value = labels.length ? labels.join(", ") : "none";
    } else {
      value = stored.trim() === "" ? "left blank" : stored;
    }
    rows.push({ key: f.key, label: f.label, value, ...(overflow ? { overflow } : {}) });
  }
  return rows;
}

export function GovFormZone({ clientId, taskKey, govForm }: { clientId: string; taskKey: string; govForm: GovForm }) {
  const framing = govForm.deliveryFraming ?? "prep";
  const copy = FRAMING_COPY[framing] ?? FRAMING_COPY.prep;
  const canDownload = framing === "real_fill" && !!govForm.documentType;
  // Ack-gated download (D-135; D-137 s137): a fill_reviewed fact-only form (docx court forms + TX19
  // real-pdf) OR a `generated` prep-sheet (EIR). supportsDraft = the form is in ACK_GATED_FORMS.
  const canDownloadDraft = (framing === "fill_reviewed" || framing === "generated") && !!govForm.supportsDraft && !!govForm.documentType;
  // s149 SEAM-FIX-1: a GENERATED, NON-ack notice (the ServiceOntario cancellations) — surfaced in-task as a
  // direct download (no ack, no gap; reuses the plain GET path). The both-places rule keeps it on the
  // Estate Documents page too. This is what makes the DL/OHIP tasks offer their own notice.
  const canDownloadGenerated = framing === "generated" && !govForm.supportsDraft && !!govForm.documentType;

  const { toast } = useToast();
  const [ackOpen, setAckOpen] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // Phase 6b (s146): gov-form gap dialog — required inputs the draft POST reports missing (422).
  const [gapFields, setGapFields] = useState<LetterGapField[] | null>(null);
  const [gapSubmitting, setGapSubmitting] = useState(false);
  // s147: executor-facing repeating-group notes (de-dupe collision / count / overflow) carried on the
  // X-Form-Notes response header (F-6 removed the companion page). Shown beside the download.
  const [formNotes, setFormNotes] = useState<string[]>([]);
  const readNotes = (res: Response) => {
    const h = res.headers.get("X-Form-Notes");
    try { setFormNotes(h ? (JSON.parse(decodeURIComponent(h)) as string[]) : []); } catch { setFormNotes([]); }
  };

  // s147 (D-146): the "Before you file this form" panel — loaded after a successful download. Best-effort
  // (never blocks the download). `changeOpen` drives the #7 route back into the stored answers.
  const [prep, setPrep] = useState<PrepPayload | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  // s148 PANEL-TIMING-1: the "before you download" surface is loaded ON MOUNT (not only after download),
  // so the static prep + the executor's current answers are visible before the button is pressed. The
  // one-line summary is collapsed by default (never crowds the button); `downloaded` gates the
  // generation-dependent post-download panel; `pendingChangeValues` threads a Change edit through the ack.
  const [downloaded, setDownloaded] = useState(false);
  const [predlOpen, setPredlOpen] = useState(true); // s149 SEAM-FIX-1: "What to know" starts expanded (a grieving executor won't hunt for a chevron)
  // s156 (SAVE-DETERMINISM-1): `changeSaved` drives the visible "Saved" confirmation shown BEFORE the
  // dialog closes. The former `pendingChangeValues` is GONE — an edit is never held in React state
  // waiting on the acknowledgment; it is persisted and confirmed before anything closes.
  const [changeSaved, setChangeSaved] = useState(false);
  const fetchPrep = async () => {
    if (!govForm.documentType) return;
    try {
      const res = await fetch(`/api/clients/${clientId}/estate-documents/${govForm.documentType}/prep`, { credentials: "include" });
      if (res.ok) setPrep(await res.json() as PrepPayload);
    } catch { /* the panel is a companion, never a blocker */ }
  };
  // Load the prep surface once when the zone mounts (documentType-guarded inside fetchPrep).
  useEffect(() => { void fetchPrep(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId, govForm.documentType]);
  const answerEcho = prep ? deriveAnswerEcho(prep.reopenFields, prep.initialValues, prep.included) : [];

  /** s156 (SAVE-DETERMINISM-1) — persist answers WITHOUT generating, and return what is now stored.
   *  Throws on any non-OK response so the caller can never mistake a failure for a save. */
  const saveAnswers = async (values: Record<string, string>): Promise<{ unsaved: string[]; stored: Record<string, string> }> => {
    const res = await fetch(
      `/api/clients/${clientId}/estate-documents/${govForm.documentType}/answers`,
      { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ answers: values }) },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({} as { message?: string }));
      throw new Error(err.message ?? "We couldn't save your changes.");
    }
    const body = await res.json().catch(() => ({} as { unsaved?: string[]; stored?: Record<string, string> }));
    // A malformed/absent body is treated as UNCONFIRMED, not as success — the whole point is that only
    // a positive confirmation closes the dialog.
    if (!Array.isArray(body.unsaved)) throw new Error("We couldn't confirm your changes were saved.");
    return { unsaved: body.unsaved, stored: body.stored ?? {} };
  };
  // #7 — re-open the prompt with the current selection, submit → append latest-wins → re-download.
  const submitChange = async (values: Record<string, string>) => {
    setDownloadError(null);
    setChangeSubmitting(true);
    try {
      // 🔴 s156 (SAVE-DETERMINISM-1) — SAVE FIRST, ALWAYS, and prove it landed.
      //
      // This used to park the edit in `pendingChangeValues`, close the dialog and open the
      // acknowledgment; nothing was persisted until the ack completed, and cancelling the ack threw the
      // edit away silently. The executor saw a dialog close — the universal signal for "saved" — and
      // their change was gone (Reade's walkthrough: an assignment reverted to "Not on this form").
      //
      // Now: persist, re-read what is STORED, and compare. A mismatch keeps the dialog OPEN with a
      // visible error. The dialog closes only after a save we have confirmed. The acknowledgment gate is
      // unchanged and still sits in front of the DOWNLOAD (D-148.7) — it just no longer owns the save.
      const { unsaved } = await saveAnswers(values);
      if (unsaved.length > 0) {
        setDownloadError(
          "We couldn't confirm your changes were saved, so nothing has been changed. Please try again.",
        );
        return; // dialog stays open, values intact — never a silent close
      }
      setChangeSaved(true);           // (c) visible confirmation BEFORE anything closes
      await fetchPrep();              // refresh the echo from the server's stored state
      await new Promise((r) => setTimeout(r, SAVED_CUE_MS));
      setChangeOpen(false);
      setChangeSaved(false);
      // Re-download: gated forms go through the acknowledgment (now carrying nothing — the answers are
      // already persisted, so an abandoned ack costs the download, never the edit).
      if (govForm.supportsDraft) { setAckOpen(true); return; }
      await runDownloadPost({});
    } catch (e) {
      reportFailure(e);
    } finally {
      setChangeSubmitting(false);
    }
  };

  // GOVFORM-ERROR-UX (s138, D-133.1): fetch/blob download that SURFACES failures — a generate 500
  // (qpdf missing, fingerprint mismatch, etc.) used to fail silently via window.open into a broken tab
  // (the s133 4×500s). Mirrors EstateDocumentsPage.handleDownload. Throws on any failure; the callers
  // render a visible error state. Never opens a blank tab on error.
  const runDownload = async (): Promise<void> => {
    const res = await fetch(
      `/api/clients/${clientId}/estate-documents/generate/${govForm.documentType}`,
      { credentials: "include" },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({} as { message?: string }));
      throw new Error(err.message ?? "We couldn't generate this form.");
    }
    readNotes(res);
    setDownloaded(true); // s148: unlock the post-download "Before you file this form" panel
    void fetchPrep(); // s147: load the "Before you file this form" panel after the download
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadFilename(res, `${govForm.title}-${govForm.formId}`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Phase 6b (s146): the draft (ack-gated) path POSTs, carrying any gap answers. A 422 means required
  // inputs are missing → surface the gap dialog (setGapFields). A 200 downloads the blob. Returns
  // whether a gap remains, so the caller keeps the dialog open until it clears.
  const runDownloadPost = async (answers: Record<string, string>): Promise<{ gap: boolean }> => {
    const res = await fetch(
      `/api/clients/${clientId}/estate-documents/generate/${govForm.documentType}`,
      { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ answers }) },
    );
    if (res.status === 422) {
      const body = await res.json().catch(() => ({} as { missingFields?: LetterGapField[] }));
      setGapFields((body.missingFields ?? []) as LetterGapField[]);
      return { gap: true };
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({} as { message?: string }));
      throw new Error(err.message ?? "We couldn't generate this form.");
    }
    readNotes(res);
    setDownloaded(true); // s148: unlock the post-download "Before you file this form" panel
    void fetchPrep(); // s147: load the "Before you file this form" panel after the download
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadFilename(res, `${govForm.title}-${govForm.formId}`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return { gap: false };
  };

  // Single failure surface for both paths: inline error in the zone + a destructive toast.
  const reportFailure = (e: unknown) => {
    const msg = (e instanceof Error && e.message) || "We couldn't generate this form. Please try again.";
    setDownloadError(msg);
    toast({ title: "Couldn't generate the form", description: msg, variant: "destructive" });
  };

  // Direct (real_fill) download — no ack.
  //
  // 🔴 Phase 6b (s147, f6aa0e1 fix): an ACTIVE prompt-flow form MUST route through the POST gap check
  // (runDownloadPost → 422 → setGapFields opens the dialog), exactly as confirmDraft does for ack-gated
  // forms. A plain GET (runDownload) skips resolveFormGaps entirely, so the prompt would never fire —
  // that is the bug where activating ISP-1300/1200/1201/CRA-NOD in PHASE_6B_ACTIVE_FORMS was inert on
  // the client (server 422 worked; the button GET'd). The gov-form-download-route-guard test pins this
  // for every active form. Non-active real_fill forms keep the plain GET (nothing to prompt for).
  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      if (govForm.formId && PHASE_6B_ACTIVE_FORMS.has(govForm.formId)) {
        await runDownloadPost({});
      } else {
        await runDownload();
      }
    } catch (e) {
      reportFailure(e);
    } finally {
      setDownloading(false);
    }
  };

  // Ack gate: record the acknowledgment (form_draft_ack), then download. On any failure — the ack POST
  // OR the generate — the dialog stays OPEN and the error is shown, rather than closing silently.
  // SINGLE-FLIGHT-PRIMITIVE-1 (s170). Not a `useMutation` — a raw async handler behind a dialog
  // Confirm, which is exactly why the primitive wraps the ACTION rather than a button: `ackBusy` is
  // React state and lags a render, so two confirms inside one tick both POST. `form_draft_ack` is
  // an append-only audit of "the executor acknowledged the lawyer-review requirement" — two acks
  // for one acknowledgment is a corrupted record, not noise.
  const ackFlight = useSingleFlight();
  // D-170.6: the request moved into a mutation. Class A-enclosed — `form_draft_ack` is an
  // append-only audit write, but it already runs inside `ackFlight` above, which covers the
  // whole confirm handler (ack POST + the generate that follows it).
  const formAckMutation = useMutation({
    mutationFn: async (formId: string) =>
      await apiRequest("POST", `/api/executor/${clientId}/tasks/${taskKey}/form-ack`, { formId }),
  });

  const confirmDraft = async () => {
    await ackFlight.run(async () => {
      setAckBusy(true);
      setDownloadError(null);
      try {
        await formAckMutation.mutateAsync(govForm.formId);
        // Phase 6b (s146): POST so an active form (74A/74.1A) can 422 for missing inputs. When it does,
        // runDownloadPost sets gapFields → the gap dialog opens as the ack dialog closes. Otherwise the
        // form downloads directly, exactly as before. s156: this carries NOTHING — a Change edit is
        // already persisted and confirmed before the acknowledgment opens, so abandoning the ack costs
        // the download, never the edit.
        await runDownloadPost({});
        setAckOpen(false);
      } catch (e) {
        reportFailure(e);
      } finally {
        setAckBusy(false);
      }
    });
  };

  // Gap dialog submit: re-POST with the collected answers. On success the form downloads and the dialog
  // closes; if inputs are still missing, runDownloadPost refreshes gapFields and the dialog stays open.
  const submitGap = async (values: Record<string, string>) => {
    setGapSubmitting(true);
    setDownloadError(null);
    try {
      const { gap } = await runDownloadPost(values);
      if (!gap) setGapFields(null);
    } catch (e) {
      reportFailure(e);
    } finally {
      setGapSubmitting(false);
    }
  };

  return (
    <div className="relative mt-4 rounded-md p-4" style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}` }}
      data-testid={`gov-form-${govForm.formId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <FileText className="h-4 w-4 shrink-0" style={{ color: SAGE[600] }} />
        {/* F-7 (s134): fill_reviewed zones LEAD with the form identity at heading weight — an executor
            told "you need Form 74A" must see it, not read it as a muted subtitle. "Prepare your
            answers" demotes to the subtitle position. One shared component → all 7 fill_reviewed
            zones. Registry title/formId only; never a hand-written per-form string (jurisdiction seam). */}
        {framing === "fill_reviewed" ? (
          <>
            <span className="text-[13px] font-semibold" style={{ color: WARM.charcoal }}>Form {govForm.formId} — {govForm.title}</span>
            <span className="text-[12px]" style={{ color: WARM.taupe }}>· {copy.heading}</span>
          </>
        ) : (
          <>
            <span className="text-[13px] font-semibold" style={{ color: WARM.charcoal }}>{copy.heading}</span>
            <span className="text-[12px]" style={{ color: WARM.taupe }}>· {govForm.title} ({govForm.formId})</span>
          </>
        )}
      </div>
      <p className="text-[12px] mt-1.5" style={{ color: WARM.taupe }} data-testid={`gov-form-blurb-${govForm.formId}`}>{copy.blurb}</p>

      {/* s148 PANEL-TIMING-1 — preparation guidance BEFORE the download (Reade's "I download first"
          finding). The executor's current answers + a way to correct them show ABOVE the button whenever a
          prior generation exists; the static per-form prep (blanks / sections-you-complete / where-it-goes)
          collapses under a one-line toggle so it never crowds the button. Loaded on mount, not post-download.
          Every line describes the FORM, never the person (D-146 / D-147.22). */}
      {prep && (
        <div className="mt-2.5" data-testid={`gov-form-predl-${govForm.formId}`}>
          {answerEcho.length > 0 && (
            /* ECHO-EMPHASIS-1 (s148): the answers box uses the brand shaded-row emphasis — sage-50 wash +
               sage-600 left accent + sage-800 medium header — so it reads as assertive, not skippable. */
            <div className="mb-2 rounded-md p-2.5 text-[12px]" style={{ backgroundColor: SAGE[50], border: `1px solid ${SAGE[200]}`, borderLeftWidth: 3, borderLeftColor: SAGE[600], color: WARM.charcoal }} data-testid={`gov-form-answers-${govForm.formId}`}>
              <div className="font-medium mb-1" style={{ color: SAGE[800] }}>Your answers on this form:</div>
              <ul className="pl-4 flex flex-col gap-0.5">
                {answerEcho.map((a) => (
                  <li key={a.key}>
                    {a.label}: <span style={{ color: WARM.taupe }}>{a.value}</span>
                    {a.overflow && (
                      <div className="mt-0.5" data-testid={`gov-form-answers-overflow-${govForm.formId}`}>
                        <span className="font-medium">Added on a continuation sheet in this download:</span> <span style={{ color: WARM.taupe }}>{a.overflow}</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {/* s156 (SAVE-DETERMINISM-1): disabled while the acknowledgment is up or a download is in
                  flight. The dialogs no longer render underneath the ack (a), so without this the button
                  would swallow the click and appear broken — a dead control is its own silent failure. */}
              <button
                type="button"
                onClick={() => setChangeOpen(true)}
                disabled={ackOpen || ackBusy || downloading}
                className="underline font-medium mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: SAGE[600] }}
                data-testid={`gov-form-answers-change-${govForm.formId}`}
              >
                Change these answers
              </button>
            </div>
          )}

          {(groupBlanks(prep.blanks ?? []).length > 0 || (prep.executorSections ?? []).length > 0 || prep.submission) && (
            <div>
              <button type="button" onClick={() => setPredlOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-[12px] font-medium"
                style={{ color: SAGE[600] }} data-testid={`gov-form-predl-toggle-${govForm.formId}`} aria-expanded={predlOpen}>
                {predlOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                What to know before you download
              </button>
              {predlOpen && (
                <div className="mt-2 rounded-md p-3 text-[12px]" style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}`, color: WARM.charcoal }} data-testid={`gov-form-predl-content-${govForm.formId}`}>
                  {groupBlanks(prep.blanks ?? []).length > 0 && (
                    <div className="mb-2.5">
                      <div className="font-medium mb-1">These are intentionally blank — you complete them on the form:</div>
                      <ul className="list-disc pl-4 flex flex-col gap-0.5" data-testid={`gov-form-predl-blanks-${govForm.formId}`}>
                        {groupBlanks(prep.blanks ?? []).map((b, i) => (
                          <li key={i}><span className="font-medium">{b.title}</span> — {b.where}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(prep.executorSections ?? []).length > 0 && (
                    <div className="mb-2.5">
                      <div className="font-medium mb-1">Sections you complete yourself:</div>
                      <ul className="list-disc pl-4 flex flex-col gap-0.5" data-testid={`gov-form-predl-sections-${govForm.formId}`}>
                        {(prep.executorSections ?? []).map((s, i) => (
                          <li key={i}><span className="font-medium">{s.section}</span> — {s.detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {prep.submission && (
                    <div data-testid={`gov-form-predl-submit-${govForm.formId}`}>
                      <span className="font-medium">Where it goes:</span> {prep.submission.line}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* s149 SIN-CASCADE-1 — the fields are all editable, but the agency still requires ink on the
          signature line; remind the executor before they file (amber = a must-do). */}
      {prep?.inkSignature && (
        <div className="mt-2.5 text-[12px] font-medium text-amber-700" data-testid={`gov-form-ink-signature-${govForm.formId}`}>
          {prep.inkSignature}
        </div>
      )}

      {/* s149 SEAM-FIX-1 — lossy-select cells the executor must pick ON the form (e.g. place of death,
          relationship). Not covered by the intentionally-blank list, so surfaced here (render-if-uncovered). */}
      {prep?.companion && prep.companion.length > 0 && (
        <div className="mt-2.5 text-[12px]" style={{ color: WARM.charcoal }} data-testid={`gov-form-companion-${govForm.formId}`}>
          <span className="font-medium">Select these on the form:</span>
          <ul className="list-disc pl-4 mt-0.5">{prep.companion.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}

      {(canDownload || canDownloadGenerated) && (
        <button type="button" onClick={handleDownload} disabled={downloading}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ backgroundColor: SAGE[600] }} data-testid={`gov-form-download-${govForm.formId}`}>
          <Download className="h-3.5 w-3.5" /> {downloading ? "Preparing…" : (canDownload ? "Download pre-filled form" : "Download form")}
        </button>
      )}

      {canDownloadDraft && (
        <button type="button" onClick={() => { setDownloadError(null); setAckOpen(true); }}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold text-white"
          style={{ backgroundColor: SAGE[600] }} data-testid={`gov-form-download-draft-${govForm.formId}`}>
          <Download className="h-3.5 w-3.5" /> Download form
        </button>
      )}

      {/* GOVFORM-ERROR-UX (s138): visible error state on a failed generate/download — previously a
          silent window.open into a broken tab (the s133 4×500s). role=alert for a11y; the retry re-runs
          the direct download. Suppressed while the ack dialog is open (the dialog shows its own error). */}
      {downloadError && !ackOpen && (
        <p className="mt-2 text-[12px] text-destructive" role="alert" data-testid={`gov-form-error-${govForm.formId}`}>
          {downloadError}
          {(canDownload || canDownloadGenerated) && (
            <> <button type="button" onClick={handleDownload} className="underline font-medium">Try again</button></>
          )}
        </p>
      )}

      {/* s147: repeating-group notes (de-dupe collision / count / overflow) surfaced after a download —
          the collision line is the one that matters (the app's category tiebreak, made visible). */}
      {formNotes.length > 0 && (
        <div className="mt-3 rounded-md p-2.5 text-[12px]" style={{ backgroundColor: "#F6F4EF", border: `1px solid ${WARM.stone}`, color: WARM.charcoal }} data-testid={`gov-form-notes-${govForm.formId}`}>
          <div className="font-semibold mb-1">About this form:</div>
          <ul className="list-disc pl-4 flex flex-col gap-0.5">
            {formNotes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}

      {/* s148 PANEL-TIMING-1 — POST-download: ONLY the generation-dependent "actually filled" check list
          remains here (the static prep + the answer echo/Change moved ABOVE the button, so nothing renders
          twice). Shown after a successful download this session. Describes the FORM, never the person. */}
      {downloaded && prep && (prep.filled ?? []).length > 0 && (
        <div className="mt-3 rounded-md p-3 text-[12px]" style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}`, color: WARM.charcoal }} data-testid={`gov-form-prep-${govForm.formId}`}>
          <div className="font-semibold mb-2">Before you file this form</div>
          <div data-testid={`gov-form-prep-filled-${govForm.formId}`}>
            <span className="font-medium">We filled these from the plan — check them before filing:</span>{" "}
            <span style={{ color: WARM.taupe }}>{prep.filled.slice(0, 8).map((f) => f.label).join(", ")}{prep.filled.length > 8 ? ", …" : ""}</span>
          </div>
        </div>
      )}

      {/* #7 — the Change dialog: re-open the prompt PRE-FILLED with the current selection. */}
      {changeOpen && prep && !ackOpen && (
        <LetterGapDialog
          fields={prep.reopenFields}
          initialValues={prep.initialValues}
          submitting={changeSubmitting}
          saved={changeSaved}
          error={downloadError}
          heading="Change your answers for this form:"
          submitLabel="Save & re-download"
          testId={`gov-form-change-dialog-${govForm.formId}`}
          variant="modal"
          onSubmit={submitChange}
          onCancel={() => setChangeOpen(false)}
        />
      )}

      <AckDialog open={ackOpen} busy={ackBusy} error={ackOpen ? downloadError : null}
        advisory={govForm.formId ? (DRAFT_ACK_ADVISORY_BY_FORM[govForm.formId] ?? null) : null}
        onConfirm={confirmDraft} onCancel={() => { setDownloadError(null); setAckOpen(false); }} />

      {/* Phase 6b (s146): the gov-form gap dialog (reused LetterGapDialog). Opens when the draft POST
          reports missing required inputs; on submit the answers write back (T1) / scribe (T2) and the
          form downloads. Only the two active docx court forms (74A/74.1A) can reach this. */}
      {gapFields && !ackOpen && (
        <LetterGapDialog
          fields={gapFields}
          submitting={gapSubmitting}
          heading="A few details before we prepare this form:"
          submitLabel="Prepare form"
          testId={`gov-form-gap-dialog-${govForm.formId}`}
          variant="modal"
          onSubmit={submitGap}
          onCancel={() => { setGapFields(null); setDownloadError(null); }}
        />
      )}

      {/* F-6 (s134): the version-check line moved here from the (removed) PDF companion page. Pairs
          with the "View the official form" source link below. */}
      {govForm.formVersion && (
        <p className="text-[11px] mt-2" style={{ color: WARM.taupe }} data-testid={`gov-form-version-${govForm.formId}`}>
          Based on {govForm.formVersion}. Check for the latest version before filing.
        </p>
      )}

      {govForm.requiredDocuments.length > 0 && (
        <div className="mt-3">
          <p className="text-[12px] font-medium" style={{ color: WARM.charcoal }}>Have these ready:</p>
          <ul className="mt-1 flex flex-col gap-1" data-testid={`gov-form-docs-${govForm.formId}`}>
            {govForm.requiredDocuments.map((d, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[13px]" style={{ color: WARM.charcoal }}>
                <CheckSquare className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: WARM.taupe }} />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {govForm.sourceUrl && (
        <a href={govForm.sourceUrl} target="_blank" rel="noopener noreferrer"
          className="mt-2.5 inline-flex items-center gap-1 text-[13px]" style={{ color: SAGE[600] }}
          data-testid={`gov-form-source-${govForm.formId}`}>
          <ExternalLink className="h-3.5 w-3.5" /> {copy.linkLabel}
        </a>
      )}
    </div>
  );
}
